import { useMemo, useCallback, useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import { userService } from '@/core/api/services/userService'
import { useGetAccount } from '@/core/hooks/account/useAccount'
import { usePaginateAdverts } from '@/core/hooks/useAdvert'
import { useSchedules } from '@/core/hooks/useSchedule'
import { useZones } from '@/core/hooks/useZone'
import { RootState } from '@/core/store'
import { useGuideTaskTracking } from '@/shared/analytics/useGuideTaskTracking'

import {
	GUIDE_TASKS,
	getRequiredTasks,
	getOptionalTasks,
	SHOW_OPTIONAL_TASKS,
} from '../constants/tasks'
import { GuideTaskCompletionKey, GuideProgress } from '../types'

const GUIDE_STORAGE_KEY = 'brandtrack_guide_progress'

/**
 * Hook to check and manage guide progress
 * Uses API data to determine task completion status
 */
export const useGuideProgress = () => {
	const { user } = useSelector((state: RootState) => state.auth)
	const { trackTaskCompletions, trackGuideCompleted } = useGuideTaskTracking()

	// Fetch current account with onboarding data
	const { data: currentAccount } = useGetAccount({
		accountId: user?.accountId,
		options: {
			enabled: !!user?.accountId,
			staleTime: 1000 * 60, // 1 minutes
		},
	})

	// Fetch zones
	const { data: zonesData } = useZones({
		config: { params: { per_page: 100 } },
		options: { enabled: true },
	})

	// Fetch adverts
	const { data: advertsData } = usePaginateAdverts({
		config: { params: { per_page: 100 } },
		options: { enabled: true },
	})

	// Fetch custom schedules (non-public schedules created by user)
	const { data: schedulesData } = useSchedules({
		config: { params: { per_page: 1, public: false } },
		options: { enabled: true },
	})

	// Fetch users to check if invited others
	const { data: usersData } = useQuery({
		queryKey: ['my-users'],
		queryFn: () => userService.getMyUsers().then((res) => res.data?.data ?? []),
		staleTime: 1000 * 60 * 5, // 5 minutes
	})

	const zones = zonesData?.data ?? []
	const adverts = advertsData?.data ?? []
	const users = usersData ?? []
	const schedules = schedulesData?.data ?? []
	const onboarding = currentAccount?.onboarding

	// Check if all required data is loaded before tracking
	const isDataReady =
		currentAccount !== undefined &&
		zonesData !== undefined &&
		advertsData !== undefined &&
		schedulesData !== undefined &&
		usersData !== undefined

	// Get persisted progress from localStorage (for exploration tasks)
	const getPersistedProgress = useCallback((): string[] => {
		try {
			const stored = localStorage.getItem(GUIDE_STORAGE_KEY)
			return stored ? JSON.parse(stored) : []
		} catch {
			return []
		}
	}, [])

	// Mark a task as completed in localStorage (for exploration tasks only)
	const markTaskCompleted = useCallback(
		(taskId: string) => {
			const current = getPersistedProgress()
			if (!current.includes(taskId)) {
				const updated = [...current, taskId]
				localStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(updated))
			}
		},
		[getPersistedProgress]
	)

	// Check if a specific task is completed based on app state
	const checkTaskCompletion = useCallback(
		(key: GuideTaskCompletionKey): boolean => {
			const persistedProgress = getPersistedProgress()

			switch (key) {
				case 'hasActiveZone':
					// Use onboarding.downloadReady from account
					return onboarding?.downloadReady ?? false

				case 'hasExploredSchedules':
					// This is tracked via localStorage when user visits schedules page
					return persistedProgress.includes('explore-schedules')

				case 'hasAssignedSchedule':
					// Use onboarding.musicAssignmentReady from account
					return onboarding?.musicAssignmentReady ?? false

				case 'hasSubscription': {
					// Check if user has an active subscription (not trial)
					// Only status 'active' counts - trial users have status 'in_trial'
					const activeSubscriptions =
						user?.account?.getActiveSubscriptions() ?? []
					return activeSubscriptions.some((sub) => sub.status === 'active')
				}

				case 'hasCreatedAdvert':
					// User has created at least one advert
					return adverts.length > 0

				case 'hasInvitedUsers':
					// User has invited at least one other user (more than just themselves)
					return users.length > 1

				case 'hasMultipleZones':
					// User has more than one zone
					return zones.length > 1

				case 'hasCreatedSchedule':
					// User has created at least one custom schedule (non-public)
					return schedules.length > 0

				default:
					return false
			}
		},
		[zones, adverts, users, schedules, user, onboarding, getPersistedProgress]
	)

	// Get completion status for all tasks
	const taskCompletionStatus = useMemo(() => {
		return GUIDE_TASKS.reduce(
			(acc, task) => {
				acc[task.id] = checkTaskCompletion(task.completionKey)
				return acc
			},
			{} as Record<string, boolean>
		)
	}, [checkTaskCompletion])

	// Calculate progress
	const progress: GuideProgress = useMemo(() => {
		const requiredTasks = getRequiredTasks()
		// Only count optional tasks if they are enabled
		const optionalTasks = SHOW_OPTIONAL_TASKS ? getOptionalTasks() : []

		const completedRequired = requiredTasks.filter(
			(task) => taskCompletionStatus[task.id]
		).length

		const completedOptional = optionalTasks.filter(
			(task) => taskCompletionStatus[task.id]
		).length

		return {
			completedTasks: Object.entries(taskCompletionStatus)
				.filter(([, completed]) => completed)
				.map(([id]) => id),
			totalRequired: requiredTasks.length,
			completedRequired,
			totalOptional: optionalTasks.length,
			completedOptional,
		}
	}, [taskCompletionStatus])

	// Get pending tasks count
	const pendingRequiredCount =
		progress.totalRequired - progress.completedRequired
	const pendingOptionalCount =
		progress.totalOptional - progress.completedOptional

	// Track task completions in PostHog
	// Only track after all data is loaded to avoid false positives
	useEffect(() => {
		if (!isDataReady) {
			return
		}

		// Track individual task completions
		trackTaskCompletions(GUIDE_TASKS, taskCompletionStatus)

		// Track when all required tasks are completed
		trackGuideCompleted(progress.totalRequired, progress.completedRequired)
	}, [
		isDataReady,
		taskCompletionStatus,
		progress.totalRequired,
		progress.completedRequired,
		trackTaskCompletions,
		trackGuideCompleted,
	])

	return {
		taskCompletionStatus,
		progress,
		pendingRequiredCount,
		pendingOptionalCount,
		markTaskCompleted,
		isTaskCompleted: (taskId: string) => taskCompletionStatus[taskId] ?? false,
		isDataReady,
	}
}
